import { maxBy } from "lodash";
import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { GridApi } from "ag-grid-community";
import { AgGridCommonFilterComponent } from "../ag-grid-common-filter/ag-grid-common-filter.component";
import { AgGridCommonSortComponent } from "../ag-grid-common-sort/ag-grid-common-sort.component";
import { AgGridCommonFilterViewItem, SortConditions } from "@shared/models/ag-grid-common-filter.model";
import { FilterConditions } from "../ag-grid-common-filter/ag-grid-common-filter.model";
import { AgGridCommonFilterService } from "@shared/services/ag-grid-common-filter.service";
import { AgGridCommonFieldsComponent } from "../ag-grid-common-fields/ag-grid-common-fields.component";
import { Subscription } from "rxjs";
import { MatMenuTrigger } from "@angular/material/menu";
import { MatDialog } from "@angular/material/dialog";
import { ShareResourceComponent, ShareDialogData, ShareDialogResult } from "../share-resource/share-resource.component";
import { PermissionsEnum } from "@shared/models/permissions";
import { AuthService } from "@auth";
import { CommonService } from "@core";
import { RolesEnum } from "@shared/models/roles";
import { TranslateService } from "@ngx-translate/core";
import { PageName } from "@shared/enums/page-name";

@Component({
  selector: "app-ag-grid-common-filter-view",
  templateUrl: "./ag-grid-common-filter-view.component.html",
  styleUrls: ["./ag-grid-common-filter-view.component.scss"],
})
export class AgGridCommonFilterViewComponent implements OnInit, OnDestroy {

  @Input() currentPageName: PageName | null = null;
  @Input() gridApi!: GridApi;
  @Input() agGridCommonFilterComponentRef!: AgGridCommonFilterComponent;
  @Input() agGridCommonSortComponentRef!: AgGridCommonSortComponent;
  @Input() aGridCommonFieldsRef!: AgGridCommonFieldsComponent;

  viewsList: AgGridCommonFilterViewItem[] = [];
  selectedView: AgGridCommonFilterViewItem | null = null;
  viewName: string = '';
  isLoaded: boolean = false;
  isSaveChanges: boolean = true;
  isCreate: boolean = false;
  isFocusInput: boolean = false;
  isSaving: boolean = false;
  editId: any = null;

  protected readonly permissions = PermissionsEnum;

  private subscriptions: Subscription[] = [];

  get isNameTooLong(): boolean {
    return this.selectedView?.name ? this.selectedView.name.length > 15 : false;
  }

  @ViewChild(MatMenuTrigger) topMenu!: MatMenuTrigger;

  constructor(
    public authService: AuthService,
    private commonService: CommonService,
    private agGridCommonFilterService: AgGridCommonFilterService,
    private dialog: MatDialog,
    private translateService: TranslateService,
  ) { }

  ngOnInit(): void {
    this.getAllViews();
    this.changesSubscriptions();
  }

  ngOnDestroy(): void {
    this.agGridCommonFilterService.saveChanges$.next(false);
    this.subscriptions.forEach((sub: Subscription) => sub?.unsubscribe());
  }

  getAllViews() {
    this.viewsList = [];
    if (this.currentPageName) {
      const filters$ = this.agGridCommonFilterService.AllFilters$.subscribe(resData => {
        if (resData.length === 0) {
          this.createDefaultView();
        }
        else {
          this.isLoaded = true;
          const currentUserEmail = this.authService.currentUserEmail;
          let data = resData.filter(el => el.owner === currentUserEmail || el.editors?.includes(currentUserEmail) || el.viewers?.includes(currentUserEmail));
          this.viewsList = data?.length ? data.sort((a, b) => (b?.updatedAt ?? 0) - (a?.updatedAt ?? 0)) : [];

          if (data.length) {
            if (!this.selectedView) {
              this.selectedView = maxBy(data, d => d.lastUsed) ?? data[0];
            }

            this.onSelectedViewChange();
          }
          else {
            this.resetFilterView();
          }
        }
      });
      this.subscriptions.push(filters$);
    }
  }

  getUserAccessLevel(item: AgGridCommonFilterViewItem):string{
    const currentUserEmail = this.authService.currentUserEmail;
    if(item.owner === currentUserEmail)
      return this.translateService.instant('gridFilter.owner');
    else if (item.editors?.includes(currentUserEmail))
      return this.translateService.instant('gridFilter.editor');
    else
      return this.translateService.instant('gridFilter.viewer');
  }

  async createDefaultView() {
    const adminUsersResponse = await this.commonService.getUsersByRoleAsync(RolesEnum.SystemAdministrator, 1);
    const adminEmail = adminUsersResponse?.users?.[0]?.email ?? '';
    if (!adminEmail) {
      this.isLoaded = true;
      return;
    }

    const allTenantUsers = await this.commonService.getAllUsersAsync() || [];
    const item: AgGridCommonFilterViewItem = {
      id: new Date().getTime() + "",
      createdAt: new Date().getTime(),
      updatedAt: new Date().getTime(),
      lastUsed: new Date().getTime(),
      name: 'Default View',
      pageName: this.currentPageName,
      filterConditions: [],
      sortConditions: [],
      fieldsAllColumnsState: [],
      filterOperator: 'AND',
      owner: adminEmail,
      editors: allTenantUsers.filter(user => user.email !== adminEmail).map(el => el.email),
      viewers: []
    };

    this.agGridCommonFilterService.addAgGridFilterView(item).then(res => {
      this.selectedView = item;
      this.agGridCommonFilterService.loadAgGridFilterView(this.currentPageName!);
    });
  }

  createView() {
    if (this.viewName) {
      this.isSaving = true;
      const item: AgGridCommonFilterViewItem = {
        id: new Date().getTime() + "",
        createdAt: new Date().getTime(),
        updatedAt: new Date().getTime(),
        lastUsed: new Date().getTime(),
        name: this.viewName,
        pageName: this.currentPageName,
        filterConditions: [],
        sortConditions: [],
        fieldsAllColumnsState: [],
        filterOperator: 'AND',
        owner: this.authService.currentUserEmail,
        editors: [],
        viewers: []
      };

      this.agGridCommonFilterService.addAgGridFilterView(item).then(() => {
        this.isCreate = false;
        this.isSaving = false;
        this.agGridCommonFilterService.loadAgGridFilterView(this.currentPageName!);
        this.resetFilterView();

        this.selectedView = item;
        this.agGridCommonFilterComponentRef.selectedView = item;
        this.agGridCommonSortComponentRef.selectedView = item;
        this.aGridCommonFieldsRef.selectedView = item;
      });

    }
  }

  saveViewChanges() {
    if (!this.selectedView?.id) {
      return;
    }

    const itemDetails: AgGridCommonFilterViewItem | undefined = this.viewsList.find((el) => el.id === this.selectedView?.id);
    if (itemDetails?.docId) {
      itemDetails.updatedAt = Date.now();
      itemDetails.filterConditions = this.agGridCommonFilterComponentRef.conditions || [];
      itemDetails.filterOperator = this.agGridCommonFilterComponentRef.operator || "AND";
      itemDetails.sortConditions = this.agGridCommonSortComponentRef.conditions || [];

      this.saveViewInItem(itemDetails);

      this.agGridCommonFilterService.saveAgGridFilterView(itemDetails).then(() => {
        this.agGridCommonFilterService.loadAgGridFilterView(this.currentPageName!);
      });
    }
  }

  renameView(viewName: string) {
    if (!viewName) {
      return;
    }
    this.updateViewData({ name: viewName, updatedAt: new Date().getTime() });
  }

  updateViewData(data: any = {}) {
    const targetId = this.editId || this.selectedView?.id;
    const itemDetails: AgGridCommonFilterViewItem | undefined = this.viewsList.find((el) => el.id === targetId)
    const docId: string | undefined = this.viewsList.find((el) => el.id === targetId)?.docId || undefined;
    if (itemDetails && itemDetails.docId) {
      this.isSaving = true;
      this.agGridCommonFilterService.updateAgGridFilterView({
        ...data,
        id: itemDetails.id,
        docId: docId,
        pageName: itemDetails.pageName
      }).then(() => {
        this.agGridCommonFilterService.loadAgGridFilterView(this.currentPageName!);
        if (this.selectedView && itemDetails.id === this.selectedView.id) {
          this.selectedView.name = data?.name || itemDetails.name
        }
        this.isSaving = false;
        this.isCreate = false;
      });
    }
  }

  deleteAgGridFilterView(id: any) {
    const doc: AgGridCommonFilterViewItem | undefined = this.viewsList.find((el) => el.id === id) || undefined;
    if (!doc) {
      return;
    }
    this.agGridCommonFilterService.deleteAgGridFilterView(doc).then(() => {
      if (this.selectedView?.id === id) {
        this.onClickItem(this.viewsList.filter(x => x.id !== id)[0]);
        this.agGridCommonFilterService.loadAgGridFilterView(this.currentPageName!);
      } else {
        this.agGridCommonFilterService.loadAgGridFilterView(this.currentPageName!);
      }
    });
  }

  saveViewInItem(item: AgGridCommonFilterViewItem) {
    if (!item) {
      return
    }

    const fieldsAllColumnsState: any[] = this.gridApi.getColumnState() || [];
    item.fieldsAllColumnsState = fieldsAllColumnsState.map((el: any) => {
      return {
        ...el,
        colRef: null,
      };
    });

    //  reset columnDefs for main filter
    if (item.filterConditions?.length) {
      item.filterConditions = item.filterConditions.map((el) => {
        return { ...el, columnDefs: [] };
      });
    }

    //  reset columnDefs for main filter
    if (item.sortConditions?.length) {
      item.sortConditions = item.sortConditions.map((el) => {
        return {
          ...el,
          columnDefs: [],
          isValid: el.isValid ?? true  // Ensure isValid is included with a default value if missing
        } as SortConditions;
      });
    }
  }

  resetFilterView() {
    this.selectedView = null;
    this.agGridCommonFilterComponentRef.conditions.length = 0;
    this.agGridCommonFilterComponentRef.validConditions = [];
    this.agGridCommonSortComponentRef.conditions.length = 0;
    this.gridApi.setFilterModel({});
    this.gridApi.resetColumnState();
    this.aGridCommonFieldsRef.reset();
  }

  onSelectedViewChange() {
    // prevent auto save data in firebase
    this.isSaveChanges = false;
    const selectedView: AgGridCommonFilterViewItem | undefined =
      this.viewsList.find(
        (el: AgGridCommonFilterViewItem) => el.id === this.selectedView?.id
      );
    if (selectedView) this.selectedView = selectedView;
    this.applySavedView();

    // enable auto save data in firebase
    setTimeout(() => { this.isSaveChanges = true; }, 300);
  }

  applySavedView() {
    if (this.selectedView) {
      this.applyMainFilter();
      this.applyMainSort();
      this.applyColsVisibility();
    }
  }

  // apply main filter
  applyMainFilter() {
    if (this.selectedView?.filterConditions) {
      let conditions: any[] =
        this.selectedView.filterConditions || [];
      conditions = conditions.map((el: FilterConditions) => {
        return {
          ...el,
          columnDefs: [
            ...(this.gridApi.getColumnDefs() || []),
          ],
        };
      });
      this.agGridCommonFilterComponentRef.selectedView = this.selectedView;
      this.agGridCommonFilterComponentRef.conditions = conditions;
      this.agGridCommonFilterComponentRef.operator = this.selectedView.filterOperator || "AND";
      this.agGridCommonFilterComponentRef.applyFilter();
    }
  }

  // apply main sort
  applyMainSort() {
    if (this.selectedView?.sortConditions) {
      let conditions: SortConditions[] = this.selectedView.sortConditions || [];
      conditions = conditions.map((el: SortConditions) => {
        return {
          ...el,
          columnDefs: [
            ...(this.gridApi.getColumnDefs() || []),
          ],
        } as SortConditions;
      });
      this.agGridCommonSortComponentRef.selectedView = this.selectedView;
      this.agGridCommonSortComponentRef.conditions = conditions;
      this.agGridCommonSortComponentRef.applySort();
    }
  }

  // apply main cols visibility
  applyColsVisibility() {
    if (this.selectedView?.fieldsAllColumnsState?.length) {
      const fieldsAllColumnsState: any[] = this.gridApi.getColumnState() || [];
      let fieldsAllColumnsStateFixed: any[] = [];
      this.selectedView?.fieldsAllColumnsState.forEach((el: any) => {
        const col = fieldsAllColumnsState.find((elx: any) => elx.colId === el.colId) || {};
        const fixedCol: any = { ...el, ...col };
        fixedCol.colRef = this.gridApi.getColumn(el.colId)?.getColDef();
        fixedCol.hide = el.hide;
        fieldsAllColumnsStateFixed.push(fixedCol);
      });
      this.gridApi.applyColumnState({
        state: fieldsAllColumnsStateFixed,
        applyOrder: true,
      });
      // aGridCommonFieldsRef
      if (fieldsAllColumnsStateFixed?.length > 0) {
        const currentColumnState = this.gridApi.getColumnState() || [];
        this.aGridCommonFieldsRef.allColumnsState = [
          // First include columns from fieldsAllColumnsStateFixed (preserved order)
          ...(fieldsAllColumnsStateFixed || [])
            .filter(el => {
              const column = this.gridApi.getColumn(el.colId);
              if (column) {
                el.colRef = column.getColDef();
                return !!el.colRef?.headerName;
              }
              return false;
            }),
          // Then append any new columns that exist in current state but not in saved state
          ...currentColumnState
            .filter(currentCol =>
              !fieldsAllColumnsStateFixed.some(savedCol => savedCol.colId === currentCol.colId)
            )
            .map(newCol => {
              const column = this.gridApi.getColumn(newCol.colId);
              return {
                ...newCol,
                colRef: column?.getColDef(),
              };
            })
            .filter(col => !!col.colRef?.headerName)
        ];
      }
      this.aGridCommonFieldsRef.checkChanges();
    }
    else {
      // Reset to original column state
      this.gridApi.resetColumnState();

      const currentColumnState = this.gridApi.getColumnState() || [];
      this.aGridCommonFieldsRef.allColumnsState = currentColumnState
        .map(col => ({
          ...col,
          colRef: this.gridApi.getColumn(col.colId)?.getColDef()
        }))
        .filter(col => !!col.colRef?.headerName);

      this.aGridCommonFieldsRef.checkChanges();
    }

    if (this.selectedView) {
      this.aGridCommonFieldsRef.selectedView = this.selectedView;
    }
  }

  createNew() {
    this.editId = null;
    this.viewName = '';
    setTimeout(() => {
      this.isCreate = true;
      this.isFocusInput = true;
    });
  }

  shareView(item: AgGridCommonFilterViewItem) {
    const dialogData: ShareDialogData = {
      name: item.name || '',
      owner: item.owner,
      editors: [...(item.editors || [])],
      viewers: [...(item.viewers || [])],
      saveFn: async (updates: ShareDialogResult) => {
        item.owner = updates.owner;
        item.editors = updates.editors;
        item.viewers = updates.viewers;
        item.updatedAt = new Date().getTime();
        await this.agGridCommonFilterService.updateAgGridFilterView(item);
      }
    };

    const dialogRef = this.dialog.open(ShareResourceComponent, {
      width: '80vw',
      maxWidth: '800px',
      panelClass: 'dialog--md',
      autoFocus: false,
      restoreFocus: false,
      closeOnNavigation: true,
      disableClose: true,
      data: dialogData
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.agGridCommonFilterService.loadAgGridFilterView(this.currentPageName!);
      }
    });
  }

  editViewName(item: AgGridCommonFilterViewItem) {
    if (!item) { return }
    this.isFocusInput = true; this.editId = item.id; this.viewName = item.name || '';
    setTimeout(() => {
      this.isCreate = true
    }, 1);
  }

  changesSubscriptions() {
    const saveChanges$ = this.agGridCommonFilterService.saveChanges$.subscribe((res => {
      if (res && this.isSaveChanges) {
        this.saveViewChanges();
      }
    }));
    this.subscriptions.push(saveChanges$);
  }

  onClickItem(item: AgGridCommonFilterViewItem) {
    if (item && this.selectedView?.id !== item.id) {
      this.selectedView = item;
      this.onSelectedViewChange();
      this.agGridCommonFilterService.updateAgGridFilterView({
        ...item,
        id: item.id,
        docId: item.docId,
        lastUsed: Date.now()
      }).then(() => {
        this.agGridCommonFilterService.loadAgGridFilterView(this.currentPageName!);
      });
      if (this.topMenu && this.topMenu.menuOpen) {
        this.topMenu.closeMenu();
      }
    }
  }

}
